/**
 * Merchant workspace shell vocabulary: sidebar navigation, the shared
 * `common` strings (relative time, counts, unavailable states), production
 * line status labels, line actions and video status labels — the sections every
 * merchant route reads. Assembled into `WorkspaceCopy` by `../copy.ts`.
 */

export const shellCopyEn = {
  nav: {
    home: 'Home',
    lines: 'Production Lines',
    videos: 'Videos',
    profile: 'Business Profile',
    settings: 'Settings',
    account: 'Account',
    language: 'العربية',
    languageAria: 'Switch interface to Arabic',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    signOut: 'Sign out',
    skip: 'Skip to content',
    workspaceNavigation: 'Workspace navigation',
    primary: 'Main navigation',
    attention: 'need attention',
    incomplete: 'incomplete',
    workspace: 'Workspace',
    workspaces: 'Workspaces',
    switchWorkspace: (name: string) => `Workspace: ${name}. Switch workspace`,
    current: 'Current',
    createWorkspace: 'Create New Workspace',
    manageWorkspaces: 'Manage Workspaces',
    yourBusiness: 'Your business',
  },
  common: {
    soon: 'Soon',
    cancel: 'Cancel',
    close: 'Close',
    tryAgain: 'Try again',
    loading: 'Loading…',
    justNow: 'Just now',
    yesterday: 'Yesterday',
    minutesAgo: (n: number) => (n === 1 ? '1 minute ago' : `${String(n)} minutes ago`),
    hoursAgo: (n: number) => (n === 1 ? '1 hour ago' : `${String(n)} hours ago`),
    daysAgo: (n: number) => (n === 1 ? '1 day ago' : `${String(n)} days ago`),
    updated: (when: string) => `Updated ${when}`,
    started: (when: string) => `Started ${when}`,
    lastUpdated: (when: string) => `Last updated ${when}`,
    runsOnServers: 'Runs on Mouja’s servers',
    noBriefYet: 'No brief yet',
    noPromptYet: 'No prompt yet',
    notAvailable: 'Not available yet',
    notEstablished: 'Not established yet',
    unavailableTitle: 'This page can’t be shown right now',
    unavailableBody:
      'This is on Mouja’s side, not yours. Nothing has changed. Try again safely.',
    backToLines: 'Back to Production Lines',
    videosCount: (n: number) => (n === 1 ? '1 video' : `${String(n)} videos`),
    linesCount: (n: number) =>
      n === 1 ? '1 Production Line' : `${String(n)} Production Lines`,
    seconds: (n: number) => `${String(n)} seconds`,
    dash: '—',
  },
  status: {
    draft: 'Draft',
    'needs-input': 'Needs input',
    analyzing: 'Analyzing',
    'ready-to-choose': 'Ready to choose',
    generating: 'Generating',
    complete: 'Complete',
    failed: 'Failed',
    active: 'Active',
    paused: 'Paused',
    archived: 'Archived',
  },
  action: {
    brief: 'Add a brief',
    profile: 'Review Business Profile',
    analysis: 'View analysis',
    recommendations: 'Review recommendations',
    card: 'Review Content Card',
    progress: 'View progress',
    videos: 'View videos',
    issue: 'Review issue',
    resume: 'Resume',
    open: 'Open',
  },
  videoStatus: {
    generating: 'Generating',
    ready: 'Ready',
    'needs-review': 'Needs review',
    failed: 'Failed',
    'needs-input': 'Needs input',
    card: 'Content Card',
  },
};

export const shellCopyAr: typeof shellCopyEn = {
  nav: {
    home: 'الرئيسية',
    lines: 'خطوط الإنتاج',
    videos: 'الفيديوهات',
    profile: 'الملف التجاري',
    settings: 'الإعدادات',
    account: 'الحساب',
    language: 'English',
    languageAria: 'Switch interface to English',
    collapse: 'طيّ القائمة الجانبية',
    expand: 'فتح القائمة الجانبية',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    signOut: 'تسجيل الخروج',
    skip: 'تخطّى للمحتوى',
    workspaceNavigation: 'التنقل في مساحة العمل',
    primary: 'التنقل الرئيسي',
    attention: 'تحتاج انتباهك',
    incomplete: 'غير مكتمل',
    workspace: 'مساحة العمل',
    workspaces: 'مساحات العمل',
    switchWorkspace: (name: string) => `مساحة العمل: ${name}. بدّل مساحة العمل`,
    current: 'الحالية',
    createWorkspace: 'ضيف مساحة عمل جديدة',
    manageWorkspaces: 'إدارة مساحات العمل',
    yourBusiness: 'نشاطك التجاري',
  },
  common: {
    soon: 'قريبًا',
    cancel: 'إلغاء',
    close: 'إغلاق',
    tryAgain: 'جرّب مرة ثانية',
    loading: 'جاري التحميل…',
    justNow: 'الحين',
    yesterday: 'أمس',
    minutesAgo: (n: number) => (n === 1 ? 'قبل دقيقة' : `قبل ${String(n)} دقائق`),
    hoursAgo: (n: number) => (n === 1 ? 'قبل ساعة' : `قبل ${String(n)} ساعات`),
    daysAgo: (n: number) => (n === 1 ? 'قبل يوم' : `قبل ${String(n)} أيام`),
    updated: (when: string) => `آخر تحديث ${when}`,
    started: (when: string) => `بدأ ${when}`,
    lastUpdated: (when: string) => `آخر تحديث ${when}`,
    runsOnServers: 'يشتغل على سيرفرات موجة',
    noBriefYet: 'ما فيه ملخص لين الحين',
    noPromptYet: 'ما فيه وصف لين الحين',
    notAvailable: 'مو متاح لين الحين',
    notEstablished: 'ما تحدد لين الحين',
    unavailableTitle: 'ما نقدر نعرض هذي الصفحة الحين',
    unavailableBody:
      'هذي من جهة موجة مو من جهتك. ما تغيّر شي، وتقدر تجرّب مرة ثانية بأمان.',
    backToLines: 'رجوع لخطوط الإنتاج',
    videosCount: (n: number) => (n === 1 ? 'فيديو واحد' : `${String(n)} فيديوهات`),
    linesCount: (n: number) => (n === 1 ? 'خط إنتاج واحد' : `${String(n)} خطوط إنتاج`),
    seconds: (n: number) => `${String(n)} ثانية`,
    dash: '—',
  },
  status: {
    draft: 'مسودة',
    'needs-input': 'محتاج منك معلومات',
    analyzing: 'قيد التحليل',
    'ready-to-choose': 'جاهز للاختيار',
    generating: 'قيد الإنشاء',
    complete: 'مكتمل',
    failed: 'فشل',
    active: 'نشط',
    paused: 'متوقف مؤقتًا',
    archived: 'مؤرشف',
  },
  action: {
    brief: 'ضيف ملخص',
    profile: 'راجع الملف التجاري',
    analysis: 'شوف التحليل',
    recommendations: 'راجع التوصيات',
    card: 'راجع بطاقة المحتوى',
    progress: 'شوف التقدم',
    videos: 'شوف الفيديوهات',
    issue: 'شوف المشكلة',
    resume: 'كمّل',
    open: 'فتح',
  },
  videoStatus: {
    generating: 'قيد الإنشاء',
    ready: 'جاهز',
    'needs-review': 'محتاج مراجعة',
    failed: 'فشل',
    'needs-input': 'محتاج منك معلومات',
    card: 'بطاقة المحتوى',
  },
};
